import {
  EnvironmentEdge,
  EnvironmentPolicies,
  EnvironmentType,
} from '@/react/portainer/environments/types';

export type PolicyId = number;

export type PolicyType =
  | 'rbac-k8s'
  | 'rbac-docker'
  | 'security-k8s'
  | 'registry-k8s'
  | 'security-docker'
  | 'setup-k8s'
  | 'setup-docker'
  | 'registry-docker'
  | 'change-confirmation'
  | 'cleanup-docker'
  | 'observability-k8s'
  | 'network-security-k8s'
  | 'pod-security-standards-k8s'
  | 'vulnerability-scanning-k8s'
  | 'automatic-namespace-provisioning-k8s';

export const POLICY_TYPES: Array<PolicyType> = [
  'rbac-k8s',
  'rbac-docker',
  'security-k8s',
  'registry-k8s',
  'security-docker',
  'setup-k8s',
  'setup-docker',
  'registry-docker',
  'change-confirmation',
  'cleanup-docker',
  'observability-k8s',
  'network-security-k8s',
  'pod-security-standards-k8s',
  'vulnerability-scanning-k8s',
  'automatic-namespace-provisioning-k8s',
] as const;

export function isPolicyType(value: unknown): value is PolicyType {
  return (
    typeof value === 'string' && POLICY_TYPES.includes(value as PolicyType)
  );
}

// UI-level policy categories (simplified for users)
export type PolicyCategory =
  | 'rbac'
  | 'security'
  | 'setup'
  | 'registry'
  | 'change-confirmation'
  | 'cleanup'
  | 'observability'
  | 'network-security'
  | 'vulnerability-scanning';

// Environment types for policy creation
export type PolicyEnvironmentType =
  | 'kubernetes'
  | 'docker'
  | 'podman'
  | 'swarm';

/**
 * Filter type for environment types in the policy type selector.
 * Used to filter policy types based on environment compatibility.
 */
export type EnvironmentTypeFilter =
  | 'all'
  | 'kubernetes'
  | 'docker'
  | 'podman'
  | 'swarm';

/**
 * Filter type for policy categories.
 */
export type CategoryFilter = 'all' | PolicyCategory;

/**
 * Message type for empty state messages.
 * Used to determine which message to display when no policy types are available.
 */
export type EmptyStateMessageType =
  | 'filtered'
  | 'all-environments'
  | 'kubernetes'
  | 'docker';

export interface PolicyStatusBreakdown {
  Applied: number;
  Warning: number;
  Failed: number;
  InProgress: number;
  NotSupported: number;
}

export interface Policy {
  Id: PolicyId;
  Name: string;
  Type: PolicyType;
  EnvironmentTypes: PolicyEnvironmentType[];
  EnvironmentGroups?: number[];
  CreatedAt: string;
  UpdatedAt: string;
  StatusBreakdown?: PolicyStatusBreakdown;
}

/**
 * Policy template - a prebuilt policy configuration that users can select
 * as a starting point when creating new policies.
 */
export interface PolicyTemplate {
  id: string;
  name: string;
  description: string;
  category: PolicyCategory;
  type: PolicyType;
  data: Record<string, unknown>;
}

// Helper function to extract category from policy type
export function getPolicyCategory(type?: PolicyType): PolicyCategory {
  if (!type) return 'security';
  if (type === 'change-confirmation') return 'change-confirmation';
  if (type === 'observability-k8s') return 'observability';
  if (type === 'network-security-k8s') return 'security';
  if (type === 'pod-security-standards-k8s') return 'security';
  if (type === 'automatic-namespace-provisioning-k8s') return 'rbac';
  if (type.startsWith('rbac')) return 'rbac';
  if (type.startsWith('security')) return 'security';
  if (type.startsWith('registry')) return 'registry';
  if (type.startsWith('setup')) return 'setup';
  if (type.startsWith('cleanup')) return 'cleanup';
  return 'security'; // fallback
}

/**
 * Checks if a policy type is for Kubernetes environments.
 * K8s policy types end with '-k8s'.
 */
export function isK8sPolicy(type: PolicyType): boolean {
  return type.endsWith('-k8s');
}

// Helper function to get display name from policy type
export function getPolicyTypeDisplayName(type: PolicyType): string {
  const category = getPolicyCategory(type);
  return getPolicyCategoryDisplayName(category);

  // Helper function to get display name for policy category
  function getPolicyCategoryDisplayName(category: PolicyCategory): string {
    if (category === 'rbac') return 'RBAC';
    if (category === 'network-security') return 'Network Security';
    if (category === 'security') return 'Security';
    if (category === 'registry') return 'Registry';
    if (category === 'change-confirmation')
      return 'Banner and change confirmation';
    if (category === 'cleanup') return 'Image cleanup';
    if (category === 'observability') return 'Observability';
    return 'Setup';
  }
}

// Helper function to get display name for environment type
export function getEnvironmentTypeDisplayName(
  environmentType: PolicyEnvironmentType
): string {
  switch (environmentType) {
    case 'kubernetes':
      return 'Kubernetes';
    case 'docker':
      return 'Docker';
    case 'podman':
      return 'Podman';
    case 'swarm':
      return 'Docker Swarm';
    default:
      return environmentType;
  }
}

export interface Directive<T> {
  value: T;
  enforce: boolean;
}

export interface TypedPolicies {
  policies: TypedPolicy[];
}
/**
 * Generic policy with typed Data field
 * Use this when you know the specific policy type and need type safety
 */
export interface TypedPolicy<TData = unknown> extends Policy {
  Data: TData;
}

export type PolicyStatus = 'active' | 'inactive' | 'error';

/**
 * Base policy metadata shared by all policies
 */
export interface BasePolicyMetadata {
  name: string;
  environmentGroups: number[];
}

/**
 * Change window configuration for GitOps updates
 */
export interface ChangeWindow {
  Enabled: boolean;
  StartTime: string; // Format: "HH:mm"
  EndTime: string; // Format: "HH:mm"
}

export interface EnvironmentInfo {
  Id: number;
  Name: string;
  Status: number;
  Type: EnvironmentType;
  URL?: string;
  Policies?: EnvironmentPolicies;
  Edge?: EnvironmentEdge;
}

/**
 * Checks if the given environment is an Edge Async environment.
 * Uses the environment info structure from the policies domain.
 *
 * @param env - The environment information object
 */
export function isEdgeAsync(env: EnvironmentInfo | null) {
  return env?.Edge?.AsyncMode ?? false;
}

/**
 * Get the minimum agent version required for a policy type
 * @param policyType - The policy type to check
 * @param metadata - Policy metadata from API containing minimum agent versions
 * @returns The minimum agent version string (e.g., "2.37.0") or null if not applicable
 */
export function getMinimumAgentVersion(
  policyType: PolicyType,
  metadata?: Record<PolicyType, string>
): string | null {
  if (!metadata) {
    return null;
  }

  const version = metadata[policyType];
  // Return null for Docker policies (0.0.0) as they don't require agent version
  if (!version || version === '0.0.0') {
    return null;
  }
  return version;
}

/**
 * Type guard to check if data is Partial Policy payload.
 * We primarily need to check that the structure of the object is correct.
 */
export function isPartialPolicyPayload<PayloadType>(
  data: unknown
): data is Partial<PayloadType> {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}
